import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = { class: "manager-notifications-ant__table-wrapper personal-area-table-wrapper" }
const _hoisted_3 = {
  key: 1,
  class: "manager-notifications-ant__reply-form"
}

import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import type { TableColumnsType } from 'ant-design-vue';
import ConfirmDialog from 'primevue/confirmdialog';
import Toast from 'primevue/toast';
import { initSearchInput } from '@/js/utils/search-input';
import Pagination from '../Pagination.vue';
import type { ManagerNotificationListItem } from './manager-notification-api';
import NotificationViewDrawer from './NotificationViewDrawer.vue';
import NotificationCreateDrawer from './NotificationCreateDrawer.vue';
import ReplyForm from './ReplyForm.vue';
import { useNotificationData } from './composables/useNotificationData';
import {
  getManagerNotificationStatusLabel,
  getManagerNotificationStatusModifier,
  isReplyNotificationStatus,
} from '../resident-notifications/map-notification';
import {
  MANAGER_LK_NOTIFICATIONS_TAB_ID,
  MANAGER_NOTIFICATIONS_SEARCH_ID,
  NOTIFICATIONS_PAGE_PARAM,
} from './manager-notification-api';

const SEARCH_MIN_LENGTH = 3;
type NotificationRow = ManagerNotificationListItem & {
  recipient: string;
  isReply: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const TABLE_SCROLL = { y: 500 };

const columns: TableColumnsType<NotificationRow> = [
  {
    title: 'Дата / время',
    key: 'createdAt',
    dataIndex: 'createdAt',
    width: 180,
  },
  {
    title: 'Получатель',
    key: 'recipient',
    dataIndex: 'to',
  },
  {
    title: 'Тема',
    key: 'subject',
    dataIndex: 'subject',
  },
  {
    title: 'Статус',
    key: 'status',
    dataIndex: 'status',
    width: 160,
  },
];

const {
  loadNotifications,
  loadResidents,
  sendReply,
  loadingNotifications,
  loadingResidents,
  recipients,
  messages,
  nav,
  errorNotifications,
  resetAndLoadFirstPage,
} = useNotificationData();

const loading = computed(() => loadingNotifications.value || loadingResidents.value);
const errorMessage = computed(() => errorNotifications.value);
const viewDrawerOpen = ref(false);
const selectedMessage = ref<ManagerNotificationListItem | null>(null);
const createDrawerOpen = ref(false);
const showReplyForm = ref(false);
const replyParentId = ref(0);
const replySubject = ref('');
const residentId = ref<string | null>(null);
const residentName = ref<string | null>(null);
const searchQuery = ref('');
const currentPage = ref(1);

let cleanupSearchInput: (() => void) | null = null;
let cleanupCreateButton: (() => void) | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let lastSubmittedSearch = '';
let searchReady = false;

const tableRows = computed((): NotificationRow[] => {
  return messages.value.map((message) => ({
    ...message,
    recipient: formatRecipient(message.to),
    isReply: isReplyNotificationStatus(message.status),
  }));
});

watch(
  [recipients, residentId],
  ([recipientsValue, residentIdValue]) => {
    if (!residentIdValue) {
      residentName.value = null;
      return;
    }

    const resident = recipientsValue.residents.find((item) => item.id === residentIdValue);
    residentName.value = resident?.name ?? null;
  },
  { immediate: true },
);

watch(
  [residentName, residentId],
  ([name, id]) => {
    const title = document.querySelector<HTMLElement>('.manager-notifications-section__title');
    if (!title) return;

    title.textContent = id && name ? `Уведомления для ${name}` : 'Уведомления';
  },
  { immediate: true },
);

watch(showReplyForm, (replyVisible) => {
  const toolbar = document.querySelector<HTMLElement>('[data-manager-notifications-toolbar]');
  const title = document.querySelector<HTMLElement>('.manager-notifications-section__title');

  if (toolbar) {
    toolbar.hidden = replyVisible;
  }

  if (title) {
    title.hidden = replyVisible;
  }
});

async function goToAllNotifications() {
  const url = new URL(window.location.href);
  url.searchParams.delete('residentId');
  window.history.replaceState({}, document.title, url.toString());
  residentId.value = null;
  residentName.value = null;
  currentPage.value = 1;
  await resetAndLoadFirstPage(null, searchQuery.value);
}

function formatRecipient(to: string | string[] | undefined): string {
  if (!to) return 'Всем';
  if (Array.isArray(to)) {
    if (to.length === 0) return '—';
    if (to.length === 1) return to[0];
    return `${to.length} резидентов`;
  }
  return to;
}

function formatNotificationDate(iso: string): string {
  if (!iso) return '—';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const datePart = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${timePart} / ${datePart}`;
}

function getRowProps(record: NotificationRow) {
  return {
    class: record.isReply ? 'manager-notifications-ant__row--reply' : '',
    onClick: () => {
      void onRowClick(record);
    },
  };
}

async function handleFormSubmit() {
  await resetAndLoadFirstPage(residentId.value, searchQuery.value);
}

async function onRowClick(message: NotificationRow) {
  selectedMessage.value = message;
  viewDrawerOpen.value = true;
}

async function handleNotificationMarkedRead() {
  await Promise.all([
    loadNotifications(residentId.value, currentPage.value, searchQuery.value),
    loadResidents(),
  ]);
}

async function handleReplySubmit(message: string) {
  try {
    await sendReply({
      parentId: replyParentId.value,
      message,
      subject: replySubject.value,
    });
    showReplyForm.value = false;
    await resetAndLoadFirstPage(residentId.value, searchQuery.value);
  } catch (error) {
    console.error('Ошибка отправки ответа:', error);
  }
}

function handleReplyCancel() {
  showReplyForm.value = false;
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  syncUrlToBrowser();
  void loadNotifications(residentId.value, page, searchQuery.value);
}

function syncSearchFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  searchQuery.value = params.get('q') ?? '';
  currentPage.value = parsePageFromUrl(params);
  residentId.value = params.get('residentId');
}

function parsePageFromUrl(params: URLSearchParams): number {
  const pageParam = params.get(NOTIFICATIONS_PAGE_PARAM);
  const match = pageParam?.match(/^page-(\d+)$/);
  return match ? Math.max(1, Number(match[1])) : 1;
}

function shouldSubmitSearch(value: string): boolean {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  if (trimmed.length >= SEARCH_MIN_LENGTH) {
    return true;
  }

  return lastSubmittedSearch.trim().length >= SEARCH_MIN_LENGTH;
}

function bindSearchInput(): void {
  const elements = initSearchInput(document, { inputId: MANAGER_NOTIFICATIONS_SEARCH_ID });
  if (!elements) {
    return;
  }

  elements.input.value = searchQuery.value;

  const scheduleSearch = (): void => {
    if (!searchReady) {
      return;
    }

    searchQuery.value = elements.input.value;

    if (!shouldSubmitSearch(searchQuery.value)) {
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
      lastSubmittedSearch = searchQuery.value;
      currentPage.value = 1;
      syncUrlToBrowser();
      void loadNotifications(residentId.value, 1, searchQuery.value);
    }, 400);
  };

  const handleClear = (): void => {
    queueMicrotask(scheduleSearch);
  };

  elements.input.addEventListener('input', scheduleSearch);
  elements.clearBtn.addEventListener('click', handleClear);

  cleanupSearchInput = () => {
    elements.input.removeEventListener('input', scheduleSearch);
    elements.clearBtn.removeEventListener('click', handleClear);
    cleanupSearchInput = null;
  };
}

function bindCreateButton(): void {
  const createBtn = document.querySelector<HTMLButtonElement>('[data-manager-notification-create]');
  if (!createBtn) {
    return;
  }

  const handleCreate = () => {
    createDrawerOpen.value = true;
  };

  createBtn.addEventListener('click', handleCreate);
  cleanupCreateButton = () => {
    createBtn.removeEventListener('click', handleCreate);
    cleanupCreateButton = null;
  };
}

function syncUrlToBrowser(): void {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('tab', String(MANAGER_LK_NOTIFICATIONS_TAB_ID));

  if (residentId.value) {
    url.searchParams.set('residentId', residentId.value);
  }

  const query = searchQuery.value.trim();
  if (query) {
    url.searchParams.set('q', query);
  }

  if (currentPage.value > 1) {
    url.searchParams.set(NOTIFICATIONS_PAGE_PARAM, `page-${currentPage.value}`);
  }

  window.history.replaceState({ tab: MANAGER_LK_NOTIFICATIONS_TAB_ID }, '', url.toString());
}

onMounted(async () => {
  syncSearchFromUrl();
  bindSearchInput();
  bindCreateButton();

  await Promise.all([
    loadNotifications(residentId.value, currentPage.value, searchQuery.value),
    loadResidents(),
  ]);

  queueMicrotask(() => {
    searchReady = true;
    lastSubmittedSearch = searchQuery.value;
  });
});

onBeforeUnmount(() => {
  cleanupSearchInput?.();
  cleanupCreateButton?.();

  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_a_table = _resolveComponent("a-table")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["manager-notifications-ant", { 'is-loading': loading.value }]),
    "aria-busy": loading.value ? 'true' : undefined
  }, [
    (!showReplyForm.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_a_table, {
              class: "manager-notifications-ant__table",
              columns: columns,
              bordered: true,
              "data-source": tableRows.value,
              loading: loading.value,
              pagination: false,
              scroll: TABLE_SCROLL,
              "custom-row": getRowProps,
              "row-key": "id"
            }, {
              bodyCell: _withCtx(({ column, record }) => [
                (column.key === 'createdAt')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createTextVNode(_toDisplayString(formatNotificationDate(record.createdAt)), 1)
                    ], 64))
                  : (column.key === 'recipient')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(_toDisplayString(formatRecipient(record.to)), 1)
                      ], 64))
                    : (column.key === 'status')
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 2,
                          class: _normalizeClass(["manager-notifications-ant__status", `manager-notifications-ant__status--${_unref(getManagerNotificationStatusModifier)(record.status)}`])
                        }, [
                          _cache[3] || (_cache[3] = _createElementVNode("span", {
                            class: "manager-notifications-ant__status-dot",
                            "aria-hidden": "true"
                          }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_unref(getManagerNotificationStatusLabel)(record.status)), 1)
                        ], 2))
                      : _createCommentVNode("", true)
              ]),
              emptyText: _withCtx(() => [
                _createTextVNode(_toDisplayString(errorMessage.value ||
              (searchQuery.value.trim() ? 'По вашему запросу ничего не найдено' : 'Уведомлений нет')), 1)
              ]),
              _: 1
            }, 8, ["data-source", "loading"])
          ]),
          (_unref(nav).totalCount > _unref(nav).pageSize)
            ? (_openBlock(), _createBlock(Pagination, {
                key: 0,
                modelValue: currentPage.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
                class: "manager-notifications-ant__pagination personal-area-table-pagination",
                "total-count": _unref(nav).totalCount,
                "page-size": _unref(nav).pageSize,
                onChange: handlePageChange
              }, null, 8, ["modelValue", "total-count", "page-size"]))
            : _createCommentVNode("", true),
          _createVNode(NotificationViewDrawer, {
            open: viewDrawerOpen.value,
            "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((viewDrawerOpen).value = $event)),
            message: selectedMessage.value,
            onMarkedRead: handleNotificationMarkedRead
          }, null, 8, ["open", "message"]),
          _createVNode(NotificationCreateDrawer, {
            open: createDrawerOpen.value,
            "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((createDrawerOpen).value = $event)),
            recipients: _unref(recipients),
            onSubmit: handleFormSubmit
          }, null, 8, ["open", "recipients"])
        ], 64))
      : (showReplyForm.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(ReplyForm, {
              "parent-id": replyParentId.value,
              subject: replySubject.value,
              onSubmit: handleReplySubmit,
              onCancel: handleReplyCancel
            }, null, 8, ["parent-id", "subject"])
          ]))
        : _createCommentVNode("", true),
    _createVNode(_unref(ConfirmDialog)),
    _createVNode(_unref(Toast))
  ], 10, _hoisted_1))
}
}

})